import React, { useEffect, useRef, useState } from "react";
import { storyAssets } from "../../constants/assets";
import { revertLastCompletedChapter } from "../../utils/storyHelpers";
import WebLottiePlayer from "../WebLottiePlayer";
import AnimatedBox from "./AnimatedBox";
import { chapterProgressBarProps } from "./types";
// @ts-ignore
import "./ChapterProgressBar.css";

const ChapterProgressBar: React.FC<chapterProgressBarProps> = ({
  chapters,
  milestones,
  currentBlockId,
  onMilestoneClick,
  isMilestoneActivated,
}) => {
  const [progressRatios, setProgressRatios] = useState<number[]>([]);
  const [isExpanded, setIsExpanded] = useState(true);
  const milestoneTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  useEffect(() => {
    const timer = setTimeout(() => setIsExpanded(false), 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const ratios = chapters.map((chapter) => {
      const completed = chapter.blocks.filter(
        (b) => b.status === "completed",
      ).length;
      return completed / Math.max(chapter.blocks.length, 1);
    });
    setProgressRatios(ratios);
  }, [chapters]);

  useEffect(() => {
    if (isMilestoneActivated) {
      let lastCompletedChapterId: number | null = null;
      for (let i = chapters.length - 1; i >= 0; i--) {
        if (chapters[i].status === "completed") {
          lastCompletedChapterId = chapters[i].id;
          break;
        }
      }
      if (!lastCompletedChapterId) return;

      const matchingMilestone = milestones.find(
        (m) => m.chapter === lastCompletedChapterId,
      );
      if (matchingMilestone) {
        milestoneTimeoutRef.current = setTimeout(() => {
          onMilestoneClick?.(matchingMilestone);
        }, 1000);
      }
    }

    return () => {
      if (milestoneTimeoutRef.current) {
        clearTimeout(milestoneTimeoutRef.current);
      }
    };
  }, [
    isMilestoneActivated,
    chapters,
    currentBlockId,
    milestones,
    onMilestoneClick,
  ]);

  const expandProgressBar = () => {
    setIsExpanded(true);
    setTimeout(() => setIsExpanded(false), 4000);
  };

  const dataValue = revertLastCompletedChapter(chapters);

  return (
    <div className="web-chapter-progress-wrapper">
      {isExpanded && (
        <div className="web-chapter-progress-row">
          {/* Bar segments — layout base */}
          <div className="web-chapter-bars">
            {chapters.map((chapter, index) => {
              const isChapterComplete = chapter.status === "completed";
              const fillPercent = isChapterComplete
                ? 100
                : (progressRatios[index] ?? 0) * 100;

              return (
                <div
                  key={`progress-${index}`}
                  className={`web-chapter-bar${
                    index === 0 ? " web-chapter-bar-left" : ""
                  }${index === chapters.length - 1 ? " web-chapter-bar-right" : ""}`}
                >
                  <div
                    className="web-chapter-bar-fill"
                    style={{ width: `${fillPercent}%` }}
                  />
                </div>
              );
            })}
          </div>

          {/* Milestone icons — absolutely overlaid at each segment's right edge */}
          <div className="web-chapter-icon-row">
            {chapters.map((chapter, index) => {
              const milestone = milestones[index];
              const isChapterComplete = chapter.status === "completed";
              const timelineIcon =
                milestone?.type?.toLowerCase() === "pledge"
                  ? (milestone?.pledge?.bgImage ?? "")
                  : (milestone?.icon ?? "");

              let iconNode = (
                <div className="web-milestone-icon-wrapper">
                  <WebLottiePlayer
                    source={timelineIcon}
                    className="web-milestone-icon"
                    autoPlay={false}
                    loop={false}
                  />
                </div>
              );

              if (isChapterComplete) {
                iconNode = (
                  <div className="web-milestone-completed">
                    <WebLottiePlayer
                      source={storyAssets.lottie.gemStarIcon}
                      className="web-milestone-completed-icon"
                      autoPlay={false}
                      loop={false}
                    />
                  </div>
                );
              }

              if (
                isMilestoneActivated &&
                milestone?.chapter === dataValue?.id
              ) {
                iconNode = (
                  <AnimatedBox isComplete imageSource={timelineIcon} />
                );
              }

              return (
                <div
                  key={`milestone-${index}`}
                  className="web-milestone-holder"
                >
                  {iconNode}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isExpanded && (
        <button
          type="button"
          className="web-chapter-expand-btn"
          onClick={expandProgressBar}
          aria-label="Expand progress"
        >
          <img
            src={storyAssets.shrinkedProgressBar}
            alt=""
            className="web-chapter-expand-icon"
          />
        </button>
      )}
    </div>
  );
};

export default ChapterProgressBar;
